import { useState, type ReactNode } from 'react';
import { TalkBubblesIcon } from './Icons.js';
import { useAi } from '../lib/ai.js';
import { TalkLive } from './TalkLive.js';
import { TypeItThrough } from './TypeItThrough.js';

/**
 * "That looked like a hard one. Want to talk it through?"
 *
 * Two things can raise it, and they are not the same act:
 *
 * - `intensity` — the person's own number on a thought record. The app is
 *   responding to what they said about themselves.
 * - `weight` — a model's read of a journal entry, because a journal has no
 *   slider and the alternative is offering nothing. This is the more intrusive
 *   of the two and it is why the setting exists; in the other two modes the
 *   judgement is never asked for, not merely ignored.
 *
 * The copy differs accordingly. Citing a number back to someone who chose it is
 * fine. Telling them a model decided their evening sounded hard needs to be said
 * plainly, without pretending to a certainty nothing has.
 *
 * Deliberately an offer and not a prompt. It appears once, under the record,
 * declines silently, and never asks again for that record. An app that noticed
 * you were struggling and then kept mentioning it would be worse company than
 * one that noticed nothing.
 *
 * What it opens is a conversation with nothing to fill in. It does not advise,
 * reassure, or reframe — see SUPPORT_INSTRUCTIONS on the server — and it is
 * told to stop and point at Help now if anything suggests real danger. It is
 * not a crisis service and does not pretend to be one; Help now is, and it is
 * on the home screen where it always was.
 */
export type TalkReason =
  /** They put a number on it themselves. */
  | { kind: 'intensity'; intensity: number }
  /** Something read the entry and thought it sounded hard. */
  | { kind: 'weight' };

/**
 * How long the offer stays up, from the moment the entry was written.
 *
 * Twelve hours covers writing something at midnight and seeing it at eight, and
 * stops there. An offer to talk through a bad evening is kind on the evening and
 * an intrusion three weeks later, when re-reading an old entry should be allowed
 * to be an ordinary act rather than something the app has opinions about.
 */
const OFFER_WINDOW_MS = 12 * 60 * 60 * 1000;

export function TalkOffer({
  reason,
  /** When the thing being offered about happened. Omitted means always offer. */
  occurredAt,
  /**
   * A second offer sharing this block — in practice "something bigger going
   * on?", which fires from the same entry for the same reason.
   *
   * They were two separate cards with the transcript between them, and on the
   * night you learn something that reads as the app asking twice about one
   * thing. Talking is offered first: company before filing.
   *
   * It OUTLIVES this component's own states. Declining a conversation, or
   * opening the entry the next morning past the twelve-hour window, must not
   * silently take the other offer with it — they are answers to different
   * questions and the second one is not time-limited, because it is about the
   * coming weeks rather than this evening.
   */
  footer,
}: {
  reason: TalkReason;
  occurredAt?: string | null;
  footer?: ReactNode;
}): JSX.Element | null {
  const { canConverse } = useAi();
  /*
   * Only offered where it can actually work.
   *
   * Gated on `aiCompanion` — the reasoning provider behind the typed
   * conversation — and NOT on whatever the spoken one needs, which is a
   * different provider entirely. Shipping a second control that names something
   * the server cannot do would be repeating the bug this replaced.
   */
  const canType = canConverse;
  /*
   * "Can it, and is it switched on" rather than "is a key configured".
   *
   * This read `user.aiReview` alone, so a lower rung on the ladder left the
   * offer on screen and it could not be accepted. Off has to mean the control
   * is gone, not that it argues with you when pressed.
   */
  const mode: 'auto' | 'off' = canConverse ? 'auto' : 'off';
  const [state, setState] = useState<'offered' | 'talking' | 'typing' | 'dismissed' | 'done'>('offered');

  const alone = (): JSX.Element | null =>
    footer ? <div className="talk-offer stack-sm stack">{footer}</div> : null;

  // Off means off. Nothing appears, nothing is judged.
  if (mode === 'off') return alone();

  // Past its window. Deliberately checked before anything renders, so an old
  // entry is simply an old entry.
  if (occurredAt) {
    const age = Date.now() - Date.parse(occurredAt);
    if (Number.isFinite(age) && age > OFFER_WINDOW_MS) return alone();
  }
  if (state === 'dismissed') return alone();

  if (state === 'typing') {
    return <TypeItThrough onExit={() => setState('done')} />;
  }

  if (state === 'talking') {
    return (
      <TalkLive
        formatKey="standard"
        mode="support"
        // Switching, not quitting. "Type instead" used to end the conversation
        // and show "nothing was saved", because there was nowhere to type.
        onTypeInstead={canType ? () => setState('typing') : undefined}
        onExit={() => setState('done')}
        // Nothing is drafted or saved from a support conversation. The
        // transcript is deliberately dropped: someone talking at midnight
        // because it hurts has not agreed to have it written down.
        onTranscript={() => setState('done')}
      />
    );
  }

  if (state === 'done') {
    return (
      <>
        <div className="card card-quiet">
          <p className="muted">
            Nothing from that conversation was saved. Your record is exactly as you left it.
          </p>
        </div>
        {alone()}
      </>
    );
  }

  /*
   * The "on request only" branch used to live here and is gone with the setting
   * that produced it. The ladder has no rung that means "converse, but form no
   * view" — talking is the top rung and it sits above reflecting — so nothing
   * could reach this any more. Left in place it would have been a screen's
   * worth of copy that no setting can display.
   */

  return (
    <div className="talk-offer stack-sm stack">
      <span className="talk-offer-mark" aria-hidden="true">
        <TalkBubblesIcon />
      </span>
      <strong>That sounded like a lot.</strong>
      <p className="muted">
        {reason.kind === 'intensity'
          ? `You put ${reason.intensity} on that.`
          : 'That is a read of what you wrote, and it might be wrong.'}{' '}
        If you want to talk it through rather than leave it written down, it's here — no forms,
        nothing saved.{canType ? ' Out loud or typed, whichever you can manage.' : ''}
      </p>
      {/* Both ways in, at equal weight. Speaking is the lower-friction path
          when executive function is spent, which is why it is first — but it is
          not always the available one, and somebody on a train or next to a
          sleeping partner has the same evening and none of the same options. */}
      <div className="row">
        <button className="btn btn-secondary grow" onClick={() => setState('talking')}>
          Talk it through
        </button>
        {canType && (
          <button className="btn btn-secondary grow" onClick={() => setState('typing')}>
            Type instead
          </button>
        )}
      </div>
      <button className="btn btn-ghost btn-block" onClick={() => setState('dismissed')}>
        Not now
      </button>
      {footer && <hr className="offer-rule" />}
      {footer}
    </div>
  );
}
